import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import {
  ArrowDownRight, ArrowUpRight, Minus, Leaf, Zap, Droplets, Trash2, Gauge,
  Sparkles, FileCheck2, AlertTriangle, CircleDot, Target, Wind, Database, FlaskConical, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TargetsDialog } from "../components/TargetsDialog";
import { FacilityBreakdown } from "../components/FacilityBreakdown";
import { NetZeroRoadmap } from "../components/NetZeroRoadmap";
import { Scope1CompositionCard } from "../components/Scope1CompositionCard";

function buildSourceUrl(sf) {
  const p = new URLSearchParams({ range: "all" });
  if (sf.pillar) p.set("pillar", sf.pillar);
  if (sf.category) p.set("category", sf.category);
  if (sf.activity) p.set("activity", sf.activity);
  return `/data-management?${p.toString()}`;
}

const GROUP_ICON = {
  "GHG Emissions": Leaf, Energy: Zap, Water: Droplets, Waste: Trash2,
  "Resource Efficiency": Gauge,
};
const RAG = {
  green: { dot: "bg-[#00E65B]", label: "On track", text: "text-[#00E65B]" },
  amber: { dot: "bg-[#FFB800]", label: "At risk", text: "text-[#FFB800]" },
  red: { dot: "bg-red-500", label: "Off track", text: "text-red-500" },
  grey: { dot: "bg-muted-foreground/40", label: "No target", text: "text-muted-foreground" },
};

function Movement({ pct, dir, lowerBetter }) {
  if (pct === null || pct === undefined) return <span className="text-muted-foreground text-xs">— vs PY</span>;
  const improving = lowerBetter ? pct < 0 : pct > 0;
  const Icon = dir === "down" ? ArrowDownRight : dir === "up" ? ArrowUpRight : Minus;
  const color = pct === 0 ? "text-muted-foreground" : improving ? "text-[#00E65B]" : "text-red-500";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`} data-testid="kpi-movement">
      <Icon className="h-3.5 w-3.5" />{pct > 0 ? "+" : ""}{pct}% <span className="text-muted-foreground font-normal">vs PY</span>
    </span>
  );
}

function KpiWidget({ w, onOpen, year }) {
  const rag = RAG[w.rag] || RAG.grey;
  const dq = w.data_quality;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(w)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(w)}
      data-testid={`env-kpi-${w.id}`}
      className="card-flat p-4 text-left cursor-pointer hover:border-accent/60 hover:bg-surface-hover transition-colors flex flex-col gap-3 group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="label-eyebrow text-[10px] mb-0.5 truncate">{w.materiality}</div>
          <div className="font-display font-semibold text-sm leading-tight">{w.name}</div>
        </div>
        <span className="flex items-center gap-1.5 shrink-0 mt-1">
          <ProvenanceTrigger pkey="env.kpi" pp={w.id} year={year} />
          <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${w.target ? rag.dot : "bg-transparent"}`} title={w.target ? rag.label : ""} />
        </span>
      </div>

      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="font-mono text-2xl font-bold tracking-tight" data-testid={`env-kpi-value-${w.id}`}>
            {formatNum(w.current, { decimals: w.unit === "%" ? 1 : 1 })}
            <span className="text-xs text-muted-foreground font-sans ml-1">{w.unit}</span>
          </div>
          <Movement pct={w.movement_pct} dir={w.movement_dir} lowerBetter={w.lower_is_better} />
          {w.coverage_change && (
            <span className="mt-1 flex items-start gap-1 text-[10px] text-sky-400" title={w.coverage_change.note} data-testid={`env-kpi-coverage-${w.id}`}>
              <Layers className="h-3 w-3 mt-px shrink-0" /> Coverage expanded — {w.coverage_change.new_scopes.map((s) => `Scope ${s}`).join(" & ")} newly tracked
            </span>
          )}
        </div>
      </div>

      {w.target ? (
        <div>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-muted-foreground inline-flex items-center gap-1">
              <Target className="h-3 w-3" /> Target {w.target.target_year}
            </span>
            <span className={`font-mono font-medium ${rag.text}`} data-testid={`env-kpi-status-${w.id}`}>{rag.label} · {w.target.progress_pct}%</span>
          </div>
          <Progress value={w.target.progress_pct} className="h-1.5" />
          {w.target.expected_value != null && (
            <div className="text-[10px] text-muted-foreground mt-1" data-testid={`env-kpi-glide-${w.id}`}>
              Glide path: {formatNum(w.target.expected_value, { decimals: 1 })} {w.unit} expected this year
            </div>
          )}
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid={`env-kpi-notarget-${w.id}`}>
          <CircleDot className="h-3 w-3" /> No target set
        </div>
      )}

      <div className="flex items-center justify-between border-t border-border pt-2 mt-auto">
        <div className="flex flex-wrap gap-1">
          {w.frameworks.slice(0, 2).map((f) => (
            <Badge key={f} variant="outline" className="text-[9px] px-1.5 py-0">{f}</Badge>
          ))}
          {w.frameworks.length > 2 && (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0">+{w.frameworks.length - 2}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground shrink-0">
          {dq.evidence && <FileCheck2 className="h-3.5 w-3.5 text-[#00E65B]" title="Evidence attached" />}
          {dq.anomaly_status === "flagged" && <AlertTriangle className="h-3.5 w-3.5 text-[#FFB800]" title="Anomaly" />}
          <span className="font-mono" title="Data quality score">DQ {dq.score}</span>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, unit, sub, icon: Icon, testid, hint, pkey, pyear }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid} title={hint}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        {Icon && <Icon className="h-4 w-4" />}
        <span className="label-eyebrow text-[10px]">{label}</span>
        {pkey && <ProvenanceTrigger pkey={pkey} year={pyear} className="ml-auto" />}
      </div>
      <div className="font-mono text-2xl font-bold tracking-tight">
        {value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span>
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function DrilldownDialog({ kpi, year, open, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commentary, setCommentary] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    if (!open || !kpi) return;
    setData(null); setCommentary("");
    setLoading(true);
    api.get(`/environment/kpi/${kpi.id}?year=${year}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [open, kpi, year]);

  const generate = async () => {
    setGenBusy(true);
    try {
      const r = await api.post(`/environment/kpi/${kpi.id}/commentary?year=${year}`);
      setCommentary(r.data.commentary);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGenBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="env-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {kpi?.name}
            <Badge variant="outline" className="text-[10px]">{kpi?.group}</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-12 text-center text-muted-foreground font-mono text-sm">Loading KPI detail…</div>
        ) : (
          <Tabs defaultValue="overview" className="mt-2">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="overview" data-testid="dd-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="trend" data-testid="dd-tab-trend">Trend</TabsTrigger>
              <TabsTrigger value="source" data-testid="dd-tab-source">Source Data</TabsTrigger>
              <TabsTrigger value="anomalies" data-testid="dd-tab-anomalies">Anomalies</TabsTrigger>
              <TabsTrigger value="audit" data-testid="dd-tab-audit">Audit</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px]">FY{year}</div>
                  <div className="font-mono text-xl font-bold">{formatNum(data.overview.current, { decimals: 1 })} <span className="text-xs text-muted-foreground">{data.unit}</span></div>
                </div>
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px]">FY{year - 1}</div>
                  <div className="font-mono text-xl font-bold">{formatNum(data.overview.previous, { decimals: 1 })} <span className="text-xs text-muted-foreground">{data.unit}</span></div>
                </div>
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px]">Movement</div>
                  <div className="text-xl"><Movement pct={data.overview.movement_pct} dir={data.overview.movement_dir} lowerBetter={data.lower_is_better} /></div>
                </div>
              </div>

              {data.overview.coverage_change && (
                <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 flex items-start gap-2" data-testid="env-drilldown-coverage">
                  <Layers className="h-4 w-4 text-sky-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-sky-100/90">{data.overview.coverage_change.note}</p>
                </div>
              )}

              <div className="card-flat p-4">
                <div className="label-eyebrow mb-1">Calculation method</div>
                <p className="text-sm text-muted-foreground">{data.calculation_method}</p>
              </div>

              {data.comparative?.target && (
                <div className="card-flat p-4">
                  <div className="label-eyebrow mb-2">Target trajectory</div>
                  <div className="text-sm font-medium">{data.comparative.target.name}</div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 mb-1">
                    <span>Baseline {data.comparative.target.baseline_year}: {formatNum(data.comparative.target.baseline_value)}</span>
                    <span>Target {data.comparative.target.target_year}: {formatNum(data.comparative.target.target_value)}</span>
                  </div>
                  <Progress value={data.comparative.target.progress_pct} className="h-2" />
                  <div className="text-xs mt-1 font-mono">{data.comparative.target.progress_pct}% to target {data.comparative.target.sbti_aligned && <Badge variant="outline" className="ml-2 text-[9px]">SBTi</Badge>}</div>
                </div>
              )}

              <div className="card-flat p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI performance commentary</div>
                  <Button size="sm" variant="primary" onClick={generate} disabled={genBusy} data-testid="dd-generate-commentary">
                    {genBusy ? "Generating…" : commentary ? "Regenerate" : "Generate"}
                  </Button>
                </div>
                {commentary
                  ? <p className="text-sm leading-relaxed" data-testid="dd-commentary-text">{commentary}</p>
                  : <p className="text-sm text-muted-foreground">Generate a plain-language explanation of this KPI's movement, likely drivers and a suggested action.</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px] mb-1">Framework alignment</div>
                  <div className="flex flex-wrap gap-1">
                    {data.frameworks.map((f) => <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>)}
                  </div>
                </div>
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px] mb-1">Data quality</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-mono text-lg font-bold">{data.data_quality.score}</span>
                    <div className="text-muted-foreground">
                      <div>Complete {data.data_quality.completeness_pct}% · Verified {data.data_quality.verification_pct}%</div>
                      <div>Status: {data.data_quality.anomaly_status}</div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="trend" className="mt-4">
              <div className="card-flat p-4">
                <div className="label-eyebrow mb-3">FY{year} monthly trend ({data.unit})</div>
                {data.trend.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={data.trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="envGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00E65B" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#00E65B" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} tickFormatter={(p) => p?.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RTooltip />
                      <Area type="monotone" dataKey="value" stroke="#00E65B" fill="url(#envGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <div className="text-sm text-muted-foreground py-8 text-center">No monthly series for this KPI.</div>}
              </div>
            </TabsContent>

            <TabsContent value="source" className="mt-4">
              {data.source_filter && (
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-xs text-muted-foreground">These values are captured in your source data table. Open it to manage, verify or add records for this measure.</p>
                  <Button variant="outline" size="sm" onClick={() => navigate(buildSourceUrl(data.source_filter))} data-testid="dd-source-link">
                    <Database className="h-3.5 w-3.5" /> View in source data table
                  </Button>
                </div>
              )}
              <div className="card-flat overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead><TableHead>Activity</TableHead>
                      <TableHead className="text-right">Value</TableHead><TableHead>Unit</TableHead>
                      <TableHead className="text-right">tCO₂e</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.source_data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{r.period}</TableCell>
                        <TableCell className="text-xs">{r.activity}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{formatNum(r.value)}</TableCell>
                        <TableCell className="text-xs">{r.unit}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.co2e_tonnes ?? "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.source || "—"}</TableCell>
                        <TableCell><Badge variant={r.status === "flagged" ? "destructive" : "outline"} className="text-[9px]">{r.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                    {!data.source_data.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">No source data.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="anomalies" className="mt-4 space-y-2">
              {data.anomalies.length ? data.anomalies.map((a, i) => (
                <div key={i} className="card-flat p-3 flex items-start gap-3">
                  <AlertTriangle className="h-4 w-4 text-[#FFB800] mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">{a.activity} · <span className="font-mono">{a.period}</span></div>
                    <div className="text-xs text-muted-foreground">{a.note}</div>
                  </div>
                </div>
              )) : <div className="text-sm text-muted-foreground py-8 text-center">No open anomalies affecting this KPI.</div>}
            </TabsContent>

            <TabsContent value="audit" className="mt-4">
              <div className="space-y-1.5">
                {data.audit_trail.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs border-b border-border pb-1.5">
                    <span className="font-mono text-muted-foreground w-16">{a.period}</span>
                    <span className="flex-1">{a.action}</span>
                    <span className="text-muted-foreground">{a.actor}</span>
                  </div>
                ))}
                {!data.audit_trail.length && <div className="text-sm text-muted-foreground py-8 text-center">No audit entries.</div>}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GasSplitDisclosure({ year }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/mobile-combustion/gas-split-summary?year=${year}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [year]);
  if (!d || !d.records) return null;
  const gases = [
    { key: "co2", label: "CO₂", t: d.co2_t, share: d.shares.co2 },
    { key: "ch4", label: "CH₄", t: d.ch4_co2e_t, share: d.shares.ch4 },
    { key: "n2o", label: "N₂O", t: d.n2o_co2e_t, share: d.shares.n2o },
  ];
  return (
    <div className="card-flat p-4" data-testid="env-gas-split">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="label-eyebrow inline-flex items-center gap-1"><FlaskConical className="h-3.5 w-3.5 text-accent" /> Mobile combustion — gas split (EPA-style disclosure)</div>
        <span className="text-[10px] text-muted-foreground">{d.gwp_set} · {d.records} records · {formatNum(d.total_t, { decimals: 2 })} tCO₂e</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {gases.map((g) => (
          <div key={g.key} data-testid={`env-gas-${g.key}`}>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">{g.label}</span>
              <span className="font-mono text-xs text-muted-foreground">{g.share}%</span>
            </div>
            <div className="font-mono text-lg font-bold tracking-tight">{formatNum(g.t, { decimals: 3 })}<span className="text-[10px] text-muted-foreground font-sans ml-1">tCO₂e</span></div>
            <div className="h-1.5 rounded-full bg-surface-hover mt-1 overflow-hidden"><div className="h-full bg-accent" style={{ width: `${Math.max(g.share, 1)}%` }} /></div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground mt-3">{d.note}</p>
    </div>
  );
}

export default function Environment() {
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [group, setGroup] = useState("all");
  const [framework, setFramework] = useState("all");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const paramView = searchParams.get("view");
  const [view, setView] = useState(["facilities", "roadmap"].includes(paramView) ? paramView : "kpis");
  const [facilityScope, setFacilityScope] = useState("fy");

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ year });
    if (group !== "all") params.set("group", group);
    if (framework !== "all") params.set("framework", framework);
    if (status !== "all") params.set("status", status);
    api.get(`/environment/dashboard?${params.toString()}`)
      .then((r) => { setData(r.data); if (r.data.available_years?.length) { setYears(r.data.available_years); if (!r.data.available_years.includes(year)) setYear(Math.max(...r.data.available_years)); } })
      .catch(() => setData({ empty: true }))
      .finally(() => setLoading(false));
  }, [year, group, framework, status]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="p-8 text-muted-foreground font-mono text-sm">Loading environment dashboard…</div>;
  }
  if (!data || data.empty) {
    return (
      <div className="p-8"><div className="card-flat p-12 text-center">
        <h2 className="font-display text-2xl font-bold">No environmental data yet.</h2>
        <p className="text-muted-foreground mt-2">Ingest emissions, energy, water and waste data to populate this dashboard.</p>
      </div></div>
    );
  }

  const s = data.summary;
  const emPct = s.total_emissions_prev ? Math.round((s.total_emissions - s.total_emissions_prev) / s.total_emissions_prev * 1000) / 10 : null;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="environment-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Control Tower · Environment · FY{year}</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
            <Wind className="h-8 w-8 text-accent" /> Environmental Performance
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Material environmental KPIs with year-on-year movement, target progress and framework alignment for {data.tenant_name}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            <Button variant={view === "kpis" ? "primary" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("kpis")} data-testid="env-view-kpis">KPIs</Button>
            <Button variant={view === "facilities" ? "primary" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("facilities")} data-testid="env-view-facilities">By Facility</Button>
            <Button variant={view === "roadmap" ? "primary" : "ghost"} size="sm" className="rounded-none" onClick={() => setView("roadmap")} data-testid="env-view-roadmap">Roadmap</Button>
          </div>
          <Button variant="outline" onClick={() => setTargetsOpen(true)} data-testid="manage-targets-btn"><Target className="h-4 w-4" /> Targets</Button>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="env-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>FY{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <SummaryTile label="Total Emissions" value={formatNum(s.total_emissions, { decimals: 0 })} unit="tCO₂e" pkey="sys.emissions_total" pyear={year}
          sub={emPct !== null ? `${emPct > 0 ? "+" : ""}${emPct}% vs PY` : undefined} icon={Leaf} testid="env-sum-emissions" />
        <SummaryTile label="Energy" value={formatNum(s.energy_mwh, { decimals: 0 })} unit="MWh" icon={Zap} testid="env-sum-energy" pkey="env.energy" pyear={year} />
        <SummaryTile label="Renewable" value={formatNum(s.renewable_pct, { decimals: 1 })} unit="%" icon={Wind} testid="env-sum-renewable" pkey="env.renewable" pyear={year} />
        <SummaryTile label="Water" value={formatNum(s.water_m3, { decimals: 0 })} unit="m³" icon={Droplets} testid="env-sum-water" pkey="env.water" pyear={year} />
        <SummaryTile label="Waste" value={formatNum(s.waste_t, { decimals: 0 })} unit="t" icon={Trash2} testid="env-sum-waste" pkey="env.waste" pyear={year} />
        <SummaryTile label="Diversion" value={formatNum(s.diversion_rate, { decimals: 1 })} unit="%" icon={Trash2} testid="env-sum-diversion" pkey="env.diversion" pyear={year} />
        <SummaryTile label="Data Quality" value={s.data_quality} unit="/100" icon={Gauge} testid="env-sum-dq" pkey="env.data_quality" pyear={year} hint={s.data_quality_method} sub="Environment records only" />
      </div>

      {view === "kpis" && <GasSplitDisclosure year={year} />}
      {view === "kpis" && <Scope1CompositionCard year={year} />}

      {/* Filters */}
      {view === "facilities" ? (
        <div className="space-y-3" data-testid="env-facilities-view">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-muted-foreground max-w-2xl">
              Emissions, energy, water and waste broken down by site — including an Unassigned bucket so data gaps are visible.
            </p>
            <div className="flex rounded-md border border-border overflow-hidden shrink-0" data-testid="env-facility-scope">
              <Button variant={facilityScope === "fy" ? "primary" : "ghost"} size="sm" className="rounded-none" onClick={() => setFacilityScope("fy")} data-testid="env-facility-scope-fy">FY{year}</Button>
              <Button variant={facilityScope === "all" ? "primary" : "ghost"} size="sm" className="rounded-none" onClick={() => setFacilityScope("all")} data-testid="env-facility-scope-all">All years</Button>
            </div>
          </div>
          <FacilityBreakdown year={facilityScope === "all" ? null : year} />
        </div>
      ) : view === "roadmap" ? (
        <NetZeroRoadmap />
      ) : (
      <>
      <div className="flex flex-wrap items-center gap-2 card-flat p-3" data-testid="env-filters">
        <span className="label-eyebrow text-[10px] mr-1">Filter</span>
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="w-44" data-testid="env-filter-group"><SelectValue placeholder="Material topic" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All material topics</SelectItem>
            {data.filters.groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={framework} onValueChange={setFramework}>
          <SelectTrigger className="w-44" data-testid="env-filter-framework"><SelectValue placeholder="Framework" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All frameworks</SelectItem>
            {data.filters.frameworks.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="env-filter-status"><SelectValue placeholder="Data status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {data.filters.statuses.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
          </SelectContent>
        </Select>
        {(group !== "all" || framework !== "all" || status !== "all") && (
          <Button variant="ghost" size="sm" data-testid="env-filter-clear"
            onClick={() => { setGroup("all"); setFramework("all"); setStatus("all"); }}>Clear</Button>
        )}
      </div>

      {/* KPI groups */}
      {data.groups.length ? data.groups.map((grp) => {
        const Icon = GROUP_ICON[grp.group] || Leaf;
        return (
          <section key={grp.group} data-testid={`env-group-${grp.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="h-5 w-5 text-accent" />
              <h2 className="font-display text-lg font-semibold">{grp.group}</h2>
              <span className="text-xs text-muted-foreground">({grp.widgets.length})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {grp.widgets.map((w) => <KpiWidget key={w.id} w={w} onOpen={setActive} year={year} />)}
            </div>
          </section>
        );
      }) : (
        <div className="card-flat p-10 text-center text-muted-foreground">No KPIs match the selected filters.</div>
      )}
      </>
      )}

      <DrilldownDialog kpi={active} year={year} open={!!active} onClose={() => setActive(null)} />
      <TargetsDialog open={targetsOpen} onClose={() => setTargetsOpen(false)} onSaved={load} />
    </div>
  );
}
